import { FC, MouseEvent, useMemo, useState } from 'react'
import { MuiEvent } from '@microservices/wiskey-react-table-component'

import { ConfiguredEntityInitialDataWrapper, ConfiguredEntityProps } from '@pages/ConfiguredEntity'
import { OpenFormProps } from '@pages/ConfiguredEntity/types'

import { useFetchDropDownFilterQuery } from '@redux/api'
import { useFetchParameterPropertiesQuery } from '@redux/api/parameters.api'
import { openFormDialog } from '@redux/reducers/dialogWindowManager.reducer'
import { addFormHistory } from '@redux/reducers/formHistory.reducer'

import {
  useAppDispatch,
  useAppSelector,
  useReplaceFieldsInFilter,
  useReplaceVariablesInFilter,
  useVariables,
} from '@hooks'
import {
  checkMaxOpenedWindowsCount,
  getInitialPositionWindow,
  getLocalStorageContext,
  transformCommonParametersToValues,
} from '@helpers'
import {
  CLICK_EVENT_TYPE,
  COMMON_PARAMETERS,
  EMBEDDED_VIEW_ROWS_AMOUNT,
  KIND_SEARCHABLE_FILTER_WORD,
} from '@constants'

import { DIALOG_WINDOW_TYPE, EntityType, formFieldsValues, GETVariableDTO } from '../../../../types'

export const EmbeddedListControl: FC<
  Omit<ConfiguredEntityProps, 'entity' | 'onSetEntity'> & {
    formId?: number
    allFieldsWithCurrentValues?: formFieldsValues
    latestVariables?: GETVariableDTO[]
  }
> = props => {
  const dispatch = useAppDispatch()
  const [entity, setEntity] = useState<EntityType | null>(null)
  const { dialogWindows, selectedId } = useAppSelector(state => state.dialogWindowManager)
  const currentDialogWindow = dialogWindows.find(dialogWindow => dialogWindow.id === selectedId)
  const context = getLocalStorageContext()

  const {
    formId,
    parentFormElementId: elementId,
    parentFormIsSectionWindow,
    parentFormObjectCode,
    parentFormObjectId,
    parentFormObjectIdType,
    allFieldsWithCurrentValues,
    onExpandedChange,
    latestVariables,
    dialogId,
  } = props

  const handleSetEntity = (value: EntityType | null) => setEntity(value)

  const globalVariables = useVariables({ isFetchAllVariables: true })

  const { data: objectFiltersListControl } = useFetchDropDownFilterQuery(
    {
      contextId: Number(context?.id),
      elementId,
    },
    {
      skip: !elementId || !context?.id,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  )

  const { updatedFilter: filterAfterReplaceFields } = useReplaceFieldsInFilter({
    allFieldsValues: allFieldsWithCurrentValues,
    objectFilters: objectFiltersListControl,
  })

  const { updatedFilter: filterAfterReplaceVariables } = useReplaceVariablesInFilter({
    rawFilter: filterAfterReplaceFields,
    latestVariables,
    globalVariables,
    searchableFilterWord: KIND_SEARCHABLE_FILTER_WORD.FORM,
  })

  const { data: commonParameters } = useFetchParameterPropertiesQuery({
    code: COMMON_PARAMETERS.STATE,
  })

  const initialCommonParameters = useMemo(
    () => transformCommonParametersToValues(commonParameters),
    [commonParameters]
  )

  const handleOpenForm = (
    { entityType, ...formProps }: OpenFormProps,
    muiEvent: MouseEvent<HTMLButtonElement> | MuiEvent<MouseEvent>,
    type: CLICK_EVENT_TYPE
  ) => {
    if (
      (type === CLICK_EVENT_TYPE.ROW_CLICK && muiEvent.shiftKey) ||
      type === CLICK_EVENT_TYPE.CREATE_CLICK
    ) {
      const { id, formCode, viewCode, objectId, path, event, objectCode, variables } = formProps

      if (checkMaxOpenedWindowsCount(initialCommonParameters)) {
        return
      }

      const initialPosition = getInitialPositionWindow(muiEvent)

      if (parentFormIsSectionWindow) {
        dispatch(
          addFormHistory({
            id,
            form: {
              formCode,
              objectCode,
              event,
              objectId: null,
              title: formCode,
              id,
              isWindow: true,
            },
          })
        )
      }

      dispatch(
        openFormDialog({
          id,
          parentDialogId: currentDialogWindow ? currentDialogWindow.id : null,
          type: DIALOG_WINDOW_TYPE.FORM,
          title: null,
          meta: {
            formCode,
            objectId,
            objectCode,
            viewCode,
            path,
            event,
            globalId: id,
            entityType,
            isIndependentForm: Boolean(parentFormIsSectionWindow),
            variables,
          },
          initialPosition,
        })
      )

      return
    }
  }

  return (
    <ConfiguredEntityInitialDataWrapper
      {...props}
      defaultFilter={filterAfterReplaceVariables}
      entity={entity}
      pageSize={EMBEDDED_VIEW_ROWS_AMOUNT}
      parentFormObjectCode={parentFormObjectCode}
      parentFormObjectId={parentFormObjectId}
      parentFormObjectIdType={parentFormObjectIdType}
      onExpandedChange={onExpandedChange}
      onOpenForm={handleOpenForm}
      onSetEntity={handleSetEntity}
    />
  )
}
